// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — Connect dialog (ABB-S7c)
// ═══════════════════════════════════════════════════════════════════════════
//
// The modal Connect panel that ENABLES the (until now inert, default-OFF) agent
// bridge. It is pure connection CHROME: it reads the live 5-state machine from
// `useConnectionStore` and drives ONLY the store's `connect` / `disconnect`
// lifecycle actions — it does NOT touch the drawing state (editorStore is NOT
// imported here, by design: a connection-state change must never perturb the
// drawing's undo/redo, see connectionStore.ts).
//
// LOOPBACK-ONLY (security): the WS target is FIXED to `AGENT_BRIDGE_WS_URL`; the
// client validates `url === wsUrl` at `connect` and throws otherwise, so the URL
// is displayed READ-ONLY — the user only supplies a pairing token. There is NO
// auto-connect: nothing opens a socket until the user clicks Connect (INV-OFF).
//
// DI is already complete in connectionStore: `connect(url, token)` internally
// calls `initAgentBridge` as its factory (captureSnapshot + safe-contour + push
// observers + onStatusChange→setStatus all defaulted inside). This dialog passes
// NONE of those — only the fixed url + the user's token.
// ═══════════════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { AGENT_BRIDGE_WS_URL } from "../agentBridge";
import { useEscapeKey } from "../hooks/useEscapeKey";
import { useConnectionStore } from "../state/connectionStore";
import { useT } from "../i18n";

export interface ConnectDialogProps {
  /** Whether the modal is open (renders `null` when false). */
  readonly open: boolean;
  /** Close affordance — invoked by the Close button, Escape, and backdrop click. */
  readonly onClose: () => void;
}

/**
 * The agent-bridge Connect modal. Reads `status` / `error` / `pairingToken` from
 * the live connection store and drives `connect` / `disconnect`.
 */
export function ConnectDialog({ open, onClose }: ConnectDialogProps) {
  const t = useT();
  const status = useConnectionStore((s) => s.status);
  const error = useConnectionStore((s) => s.error);

  // Local controlled token input. Pre-seeded from the store's last requested
  // token (`pairingToken`) ONLY on the rising open edge so a re-open shows what
  // was entered before; the user edits it freely. NOT bound to the store on each
  // keystroke — only `connect` commits it.
  const [token, setToken] = useState<string>(
    () => useConnectionStore.getState().pairingToken ?? "",
  );

  // Re-seed the token from the store ONLY on the rising open edge. The seed reads
  // `pairingToken` via `getState()` (a one-shot read, NOT a reactive
  // subscription) so a later store change while the dialog is open — e.g.
  // Disconnect nulling `pairingToken` — can NOT retrigger this effect and blank
  // the typed value. `wasOpen` tracks the previous `open` so the body runs once
  // per open transition, never on an in-place store mutation.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setToken(useConnectionStore.getState().pairingToken ?? "");
    }
    wasOpen.current = open;
  }, [open]);

  // Escape closes (only while open) — shared one-truth handler.
  useEscapeKey(onClose, open);

  if (!open) return null;

  const trimmedToken = token.trim();
  const isConnecting = status === "connecting";
  // Connect is allowed only with a non-empty token and not mid-handshake.
  const connectDisabled = trimmedToken.length === 0 || isConnecting;
  // Disconnect is meaningful in any non-idle state (a live / connecting /
  // errored handle exists or is being torn down).
  const canDisconnect =
    status === "connecting" ||
    status === "connected" ||
    status === "paired" ||
    status === "error";

  function handleConnect(): void {
    if (connectDisabled) return;
    // The URL is FIXED to the loopback target — the client refuses any other.
    useConnectionStore.getState().connect(AGENT_BRIDGE_WS_URL, trimmedToken);
  }

  function handleDisconnect(): void {
    useConnectionStore.getState().disconnect();
  }

  const statusText = t(`agent_connect.status.${status}`);

  // Portal to <body>: the dialog is mounted from inside <EditorHeader>, whose
  // `.hero-card` class carries `backdrop-filter: blur(16px)` (styles.css:138-139)
  // — that establishes a STACKING CONTEXT, so a `position:fixed` overlay rendered
  // as a header descendant is TRAPPED below the later-painted `.ribbon-shell`
  // (also a backdrop-filter context) regardless of its z-index. Portaling to
  // document.body lifts the overlay into the root stacking context so its
  // z-index:110 paints above the ribbon (verified: ribbon-quickbar was
  // intercepting the Connect button's pointer events before this).
  return createPortal(
    <div
      className="dsettings-overlay"
      data-testid="agent-connect-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="agent-connect-title"
      onClick={(e) => {
        // Backdrop click (outside the card) closes; clicks inside the card do not.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dsettings-content dsettings-subdialog">
        <div className="dsettings-title" id="agent-connect-title">
          {t("agent_connect.dialog.title")}
        </div>

        {/* URL — read-only loopback target (the user cannot re-point it). */}
        <div className="agent-connect-row">
          <label className="agent-connect-label" htmlFor="agent-connect-url">
            {t("agent_connect.label.url")}
          </label>
          <input
            id="agent-connect-url"
            data-testid="agent-connect-url"
            className="agent-connect-input"
            type="text"
            value={AGENT_BRIDGE_WS_URL}
            readOnly
          />
        </div>

        {/* Pairing token — the only user-supplied field. */}
        <div className="agent-connect-row">
          <label className="agent-connect-label" htmlFor="agent-connect-token">
            {t("agent_connect.label.token")}
          </label>
          <input
            id="agent-connect-token"
            data-testid="agent-connect-token-input"
            className="agent-connect-input"
            type="text"
            autoComplete="off"
            spellCheck={false}
            // Focus-on-open: the token field is the only editable input, so
            // opening the dialog lands the caret here (matches RenameBlockDialog).
            autoFocus
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !connectDisabled) {
                e.preventDefault();
                handleConnect();
              }
            }}
          />
        </div>

        {/* Consent line — the bearer-token field grants edit rights to any local
            process that knows the token; surface that security note inline. */}
        <p
          className="agent-connect-consent-line"
          data-testid="agent-connect-consent-line"
        >
          {t("agent_connect.consent.body")}
        </p>

        {/* Status line — localized state + the error detail when in `error`. */}
        <div className="agent-connect-row">
          <span className="agent-connect-label">
            {t("agent_connect.label.status")}
          </span>
          <span
            className={`agent-connect-status agent-connect-status-${status}`}
            data-testid="agent-connect-status"
            role="status"
            aria-live="polite"
          >
            {statusText}
            {status === "error" && error
              ? ` — ${t(`agent_connect.error.${error}`)}`
              : ""}
          </span>
        </div>

        <div className="dsettings-footer">
          {canDisconnect ? (
            <button
              type="button"
              className="dsettings-btn"
              data-testid="agent-connect-disconnect"
              onClick={handleDisconnect}
            >
              {t("agent_connect.btn.disconnect")}
            </button>
          ) : null}
          <button
            type="button"
            className="dsettings-btn"
            data-testid="agent-connect-cancel"
            onClick={onClose}
          >
            {t("agent_connect.btn.close")}
          </button>
          <button
            type="button"
            className="dsettings-btn primary"
            data-testid="agent-connect-submit"
            onClick={handleConnect}
            disabled={connectDisabled}
          >
            {t("agent_connect.btn.connect")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
